import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { 
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  CircularProgress,
  Typography,
  ListItemButton,
  Stack,
  Button
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import axios from 'axios';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Import components
import Dashboard from './components/Dashboard';
import PartnerReport from './components/PartnerReport';
import Leaderboard from './components/Leaderboard';
import Individual from './components/Individual';
import PartnerOpportunities from './components/PartnerOpportunities';
import InterlockTracker from './components/InterlockTracker';
import ErrorBoundary from './components/ErrorBoundary';

const drawerWidth = 240;

function App() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('Meeting Date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Define fetchData using useCallback to prevent infinite loops
  const fetchData = useCallback(async () => {
    try {
      console.log('=== Starting data fetch ===');
      setLoading(true);
      setError(null);
      
      // Single request without pagination
      console.log('Making API request...');
      const response = await axios.get('https://sheets-dashboard-backend.gardner-johnson.workers.dev/api/meetings');
      
      // Check if response has the expected structure
      if (!response.data || !Array.isArray(response.data.meetings)) {
        throw new Error('Invalid data format received from API');
      }
      
      // Process meetings data
      const meetings = response.data.meetings.filter(meeting => {
        const isValid = meeting && 
                       typeof meeting === 'object' &&
                       (meeting['Meeting Date'] || meeting['Timestamp']);
        if (!isValid) {
          console.warn('Invalid meeting data:', meeting);
        }
        return isValid;
      });
      
      console.log('Data fetched and processed:', {
        totalMeetings: meetings.length
      });
      
      setData(meetings);
      setLoading(false);
      console.log('=== Fetch completed successfully ===');
    } catch (err) {
      console.error('=== Fetch error details ===');
      console.error('Error type:', err.constructor.name);
      console.error('Error message:', err.message);
      
      // Enhanced error messaging
      let errorMessage = 'Failed to fetch data';
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = 'API endpoint not found. Please check if the backend service is deployed correctly.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access forbidden. Please check your permissions.';
        } else {
          errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
        }
        console.error('Response error:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
      } else if (err.message.includes('Network Error')) {
        errorMessage = 'Unable to connect to the backend service. Please check your internet connection.';
      }
      
      setError(errorMessage);
      setLoading(false);
      
      // Only retry on network errors, not on API errors
      if (err.message.includes('Network Error')) {
        console.log('Network error detected, will retry in 5 seconds...');
        setTimeout(() => {
          console.log('Retrying fetch...');
          fetchData();
        }, 5000);
      }
    }
  }, []); // No dependencies needed as we're only using external APIs and state setters

  useEffect(() => {
    // Test backend connection
    const testBackend = async () => {
      try {
        console.log('Testing backend connection...');
        const response = await axios.get('https://sheets-dashboard-backend.gardner-johnson.workers.dev/api/meetings?page=1', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log('Backend test response:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          data: response.data ? {
            total: response.data.total,
            page: response.data.page,
            totalPages: response.data.totalPages,
            sampleMeeting: response.data.meetings?.[0]
          } : null
        });
      } catch (err) {
        console.error('Backend test error:', {
          name: err.name,
          message: err.message,
          response: err.response ? {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data
          } : null
        });
      }
    };
    testBackend();
  }, []); // Run once on mount

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Add fetchData to dependencies

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleLogMeeting = () => {
    window.open('https://script.google.com/a/macros/codeium.com/s/AKfycbz6iSZU1aJTHnjLcLEwvMzo-RT7AAHuzVf5zvkX2487JOzWmZa1GI-3Hjwy7mZ5K5TD/exec', '_blank');
  };

  // Load more data when needed
  const loadMore = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Stack spacing={2} alignItems="center">
          <Typography color="error">Error: {error}</Typography>
          <Button variant="contained" onClick={() => {
            setError(null);
            fetchData();
          }}>
            Retry
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <Box sx={{ display: 'flex' }}>
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
              },
            }}
          >
            <Box sx={{ overflow: 'auto', mt: 2 }}>
              <List>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/">
                    <ListItemIcon>
                      <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                  </ListItemButton>
                </ListItem>
                
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/partner-report">
                    <ListItemIcon>
                      <AssessmentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Partner Report" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/leaderboard">
                    <ListItemIcon>
                      <TrendingUpIcon />
                    </ListItemIcon>
                    <ListItemText primary="Leaderboard" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/individual">
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText primary="Individual View" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/opportunities">
                    <ListItemIcon>
                      <AddIcon />
                    </ListItemIcon>
                    <ListItemText primary="Opportunities" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/interlock-tracker">
                    <ListItemIcon>
                      <TimelineIcon />
                    </ListItemIcon>
                    <ListItemText primary="Interlock Tracker" />
                  </ListItemButton>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemButton onClick={handleLogMeeting}>
                    <ListItemIcon>
                      <AddIcon />
                    </ListItemIcon>
                    <ListItemText primary="Log Meeting" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </Drawer>

          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
              <Routes>
                <Route 
                  path="/" 
                  element={
                    <ErrorBoundary>
                      <Dashboard 
                        data={data}
                        orderBy={orderBy}
                        order={order}
                        handleSort={handleSort}
                        loading={loading}
                        error={error}
                        loadMore={loadMore}
                        totalPages={totalPages}
                        page={page}
                      />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  path="/partner-report" 
                  element={
                    <ErrorBoundary>
                      <PartnerReport data={data} loading={loading} error={error} />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  path="/leaderboard" 
                  element={
                    <ErrorBoundary>
                      <Leaderboard data={data} loading={loading} error={error} />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  path="/individual" 
                  element={
                    <ErrorBoundary>
                      <Individual data={data} loading={loading} error={error} />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  path="/opportunities" 
                  element={
                    <ErrorBoundary>
                      <PartnerOpportunities />
                    </ErrorBoundary>
                  } 
                />
                <Route 
                  path="/interlock-tracker" 
                  element={
                    <ErrorBoundary>
                      <InterlockTracker />
                    </ErrorBoundary>
                  } 
                />
              </Routes>
            </Container>
          </Box>
        </Box>
      </Router>
    </LocalizationProvider>
  );
}

export default App;
